import { useSelector } from "react-redux";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import {
  getTwoFactorAuthSetupData,
  selectLoading,
  selectTwoFactorAuthSetupData,
  selectVerifying,
} from "./TwoFactorAuthenticationSlice";
import OtpInput from "react-otp-input";
import "./TwoFactorAuthentication.scss";
import { toast } from "react-toastify";
import { TwoFactorAuthenticationTypes } from "global/enums";
import {
  getOtherAuthenticatorTypes,
  getTwoFactorAuthDisplay,
  getTwoFactorAuthText,
  getTwoFactorVerificationFunction,
} from "./TwoFactorAuthHelpers";
import AuthenticatorTypeSelection from "./AuthenticatorTypeSelection";

type TwoFactorAuthChangePageProps = {
  currentAuthenticatorType: TwoFactorAuthenticationTypes;
  onSuccess: () => void;
};

const TwoFactorAuthChangePage = (props: TwoFactorAuthChangePageProps) => {
  const twoFactorAuthSetupData = useSelector(selectTwoFactorAuthSetupData);
  const loading = useSelector(selectLoading);
  const verifying = useSelector(selectVerifying);
  const [oneTimePassword, setOneTimePassword] = useState("");
  const [authenticatorType, setAuthenticatorType] = useState(props.currentAuthenticatorType);
  const [availableAuthenticatorTypes, setAvailableAuthenticatorTypes] = useState(
    getOtherAuthenticatorTypes(props.currentAuthenticatorType)
  );

  const thunkDispatch = useThunkDispatch();

  useEffect(() => {
    thunkDispatch(getTwoFactorAuthSetupData());
  }, [thunkDispatch]);

  useEffect(() => {
    setAvailableAuthenticatorTypes(getOtherAuthenticatorTypes(props.currentAuthenticatorType));
  }, [props.currentAuthenticatorType]);

  useEffect(() => {
    if (availableAuthenticatorTypes && availableAuthenticatorTypes.length > 0) {
      setAuthenticatorType(availableAuthenticatorTypes[0]);
    }
  }, [availableAuthenticatorTypes]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifying) {
      return;
    }

    const verified = (await thunkDispatch(getTwoFactorVerificationFunction(authenticatorType, oneTimePassword)))
      .payload;

    if (verified) {
      toast.success("2fa type changed");
      props.onSuccess();
    } else {
      toast.error("Invalid code");
    }
  };

  const onAuthenticationTypeChanged = (e: ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.currentTarget.value);
    setAuthenticatorType(index);
  };

  return (
    <div className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed">
      {/* begin::Content */}
      <div className="d-flex flex-center flex-column flex-column-fluid">
        {/* begin::Wrapper */}
        <div className="bg-white rounded p-10 mx-auto">
          <ActivityIndicator isBusy={loading}>
            <div className="card-header">
              <div className="card-title m-0">
                <h3 className="m-0 heading-text">Two Factor Authentication Setup</h3>
              </div>
            </div>
            <div className="card-body border-top p-6 d-flex flex-column gap-4 justify-content-center">
              <AuthenticatorTypeSelection
                onTypeChanged={onAuthenticationTypeChanged}
                availableAuthenticatorTypes={availableAuthenticatorTypes ?? undefined}
                data-test-id="two-factor-change-form__authenticator-type-select"
              />
              {twoFactorAuthSetupData && (
                <Form className="d-flex flex-column" style={{ height: 500 }} onSubmit={onSubmit}>
                  {getTwoFactorAuthDisplay(twoFactorAuthSetupData, authenticatorType)}
                  <div className="separator mb-2 mt-auto" />
                  <div className="verification-code-section">
                    <div className="verification-code-label">
                      Enter verification code from {getTwoFactorAuthText(authenticatorType)}:{" "}
                    </div>
                    <OtpInput
                      inputStyle="inputStyle"
                      value={oneTimePassword}
                      onChange={setOneTimePassword}
                      numInputs={6}
                      inputType="number"
                      renderSeparator={(index) => (index === 2 ? <span>-</span> : "")}
                      renderInput={(props, index) => (
                        <input {...props} data-test-id={`two-factor-change-form__code-input-${index}`} />
                      )}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="submit"
                      className="cpl-btn cpl-btn-primary mt-2"
                      data-test-id="two-factor-change-form__submit-button"
                    >
                      Confirm
                    </button>
                  </div>
                </Form>
              )}
            </div>
          </ActivityIndicator>
        </div>
        {/* end::Wrapper */}
      </div>
      {/* end::Content */}
    </div>
  );
};

export default TwoFactorAuthChangePage;
